import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  ArrowDown,
  ArrowUp,
  BarChart3,
  Copy,
  DollarSign,
  Globe,
  Hash,
  LayoutGrid,
  LineChart,
  Lock,
  Package,
  PieChart,
  Plus,
  RotateCcw,
  Search,
  Share2,
  Sparkles,
  Star,
  Table,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import {
  ANALYTICS_WIDGET_ID_SET,
  ANALYTICS_WIDGET_SIZE_OPTIONS,
  DEFAULT_ANALYTICS_WIDGETS,
  DEFAULT_ANALYTICS_WIDGET_IDS,
  getAnalyticsWidgetDefinition,
  normalizeAnalyticsWidgetHeight,
  normalizeAnalyticsWidgetWidth,
  type AnalyticsWidgetCategory,
  type AnalyticsWidgetId,
} from './analytics-dashboard-widgets';
import { canCustomizeAnalytics } from './analytics-permissions';
import { Tooltip } from '../../components/ui/tooltip';
import { analyticsApi } from '../../lib/api-client';
import { useAuthStore } from '../../stores/auth-store';

interface ReportTemplate {
  key: string;
  name: string;
  category: string;
  description: string;
  visualization: string;
  metrics: string[];
  dimensions: string[];
  requiredIntegration?: string;
  available: boolean;
}

interface SavedReport {
  id: string;
  ownerUserId: string;
  name: string;
  visibility: 'PRIVATE' | 'ORGANIZATION';
  version: number;
  templateKey: string | null;
  definition: { metrics?: string[]; dimensions?: string[]; visualization?: string };
}

interface DashboardSection {
  id: string;
  reportId: string | null;
  templateKey: string | null;
  title: string | null;
  position: number;
  width: number;
  height: number;
  visualization: string;
  layout?: { widgetKey?: string } | null;
}

interface Dashboard {
  id: string;
  name: string;
  version: number;
  sections: DashboardSection[];
}
interface AnalyticsWidgetLayoutSnapshot {
  ids: AnalyticsWidgetId[];
  sizes: Partial<Record<AnalyticsWidgetId, { width: number; height: number }>>;
}

interface MetricCatalog {
  metrics: Array<{ key: string; name: string; format: 'number' | 'percent' | 'currency'; source: string; dimensions: string[] }>;
  dimensions: Array<{ key: string; name: string }>;
}
interface ReportDraft { name: string; description: string; templateKey?: string; metrics: string[]; dimensions: string[]; visualization: string; visibility: 'PRIVATE' | 'ORGANIZATION' }
export type DashboardSurface = 'ANALYTICS' | 'HOME';

export function ReportLibraryPanel({ onClose, surface = 'ANALYTICS', mode = 'sidebar' }: { onClose?: () => void; surface?: DashboardSurface; mode?: 'sidebar' | 'drawer' }): React.JSX.Element {
  const client = useQueryClient();
  const userId = useAuthStore((state) => state.user?.id);
  const role = useAuthStore((state) => state.role);
  const canCustomize = canCustomizeAnalytics(role);
  const [search, setSearch] = React.useState('');
  const [editingTemplate, setEditingTemplate] = React.useState<ReportTemplate | null>(null);
  const [previousAnalyticsWidgetLayout, setPreviousAnalyticsWidgetLayout] = React.useState<AnalyticsWidgetLayoutSnapshot | null>(null);
  const templates = useQuery({
    queryKey: ['analytics', 'report-templates'],
    staleTime: 15 * 60_000,
    queryFn: async () => (await analyticsApi.get<ReportTemplate[]>('/analytics/report-templates')).data,
  });
  const reports = useQuery({
    queryKey: ['analytics', 'saved-reports'],
    queryFn: async () => (await analyticsApi.get<SavedReport[]>('/analytics/reports')).data,
  });
  const dashboards = useQuery({
    queryKey: ['analytics', 'dashboards', surface],
    queryFn: async () => (await analyticsApi.get<Dashboard[]>('/analytics/dashboards', { params: { surface } })).data,
  });
  const catalog = useQuery({
    queryKey: ['analytics', 'metric-catalog'],
    staleTime: 15 * 60_000,
    queryFn: async () => (await analyticsApi.get<MetricCatalog>('/analytics/metric-catalog')).data,
  });
  const dashboard = dashboards.data?.[0];
  const reportSections = dashboard?.sections.filter(isReportSection) ?? [];
  const layoutSections = dashboard?.sections.filter(isWidgetSection) ?? [];
  const analyticsWidgetIds = surface === 'ANALYTICS' ? widgetOrderFromDashboard(dashboard) : [];
  const analyticsWidgetIdSet = new Set(analyticsWidgetIds);
  const analyticsWidgetLayouts = widgetLayoutById(dashboard);
  const templateIsAdded = (templateKey: string): boolean => reportSections.some((section) => section.templateKey === templateKey);
  const reportIsAdded = (reportId: string): boolean => reportSections.some((section) => section.reportId === reportId);

  const refreshDashboard = (): Promise<void> => client.invalidateQueries({ queryKey: ['analytics', 'dashboards', surface] });
  const saveSections = async (sections: DashboardSection[]): Promise<unknown> => {
    if (!dashboard) {
      return analyticsApi.post('/analytics/dashboards', {
        name: surface === 'HOME' ? 'Home dashboard' : 'Marketing dashboard',
        surface,
        visibility: 'PRIVATE',
        sections: sections.map(toSectionPayload),
      });
    }
    return analyticsApi.post(`/analytics/dashboards/${dashboard.id}/sections`, {
      version: dashboard.version,
      sections: sections.map(toSectionPayload),
    });
  };
  const saveLayout = useMutation({
    mutationFn: async (sections: DashboardSection[]) => {
      return saveSections(mergeSectionKind(dashboard?.sections ?? [], sections, isReportSection));
    },
    onSuccess: refreshDashboard,
    onError: () => toast.error('Dashboard changed or could not be saved. Reload and retry.'),
  });
  const saveWidgetLayout = useMutation({
    mutationFn: async ({ ids, overrides }: { ids: AnalyticsWidgetId[]; overrides?: Partial<Record<AnalyticsWidgetId, { width: number; height: number }>> }) => saveSections(
      mergeSectionKind(
        dashboard?.sections ?? [],
        widgetSectionsFromOrder(ids, layoutSections, overrides),
        isWidgetSection,
      ),
    ),
    onSuccess: () => {
      refreshDashboard();
      toast.success('Analytics cards updated');
    },
    onError: () => toast.error('Analytics card layout could not be saved. Reload and retry.'),
  });

  const addTemplate = useMutation({
    mutationFn: async (template: ReportTemplate) => {
      if (!dashboard) {
        return analyticsApi.post('/analytics/dashboards', {
          name: surface === 'HOME' ? 'Home dashboard' : 'Marketing dashboard',
          surface,
          visibility: 'PRIVATE',
        sections: [{ templateKey: template.key, position: 0, width: 2, height: 2, visualization: template.visualization }],
      });
      }
      if (templateIsAdded(template.key)) return false;
      return saveLayout.mutateAsync([...reportSections, {
        id: `new-${template.key}`,
        reportId: null,
        templateKey: template.key,
        title: template.name,
        position: reportSections.length,
        width: 2,
        height: 2,
        visualization: template.visualization,
      }]);
    },
    onSuccess: (result) => {
      if (result === false) return;
      refreshDashboard();
      scrollToNewReportCard();
      toast.success('Report added to dashboard');
    },
  });
  const addSavedReport = useMutation({
    mutationFn: async (report: SavedReport) => {
      const visualization = report.definition.visualization ?? 'table';
      if (!dashboard) {
        return analyticsApi.post('/analytics/dashboards', { name: surface === 'HOME' ? 'Home dashboard' : 'Marketing dashboard', surface, visibility: 'PRIVATE', sections: [{ reportId: report.id, position: 0, width: 2, height: 2, visualization }] });
      }
      if (reportIsAdded(report.id)) return false;
      return saveLayout.mutateAsync([...reportSections, { id: `new-${report.id}`, reportId: report.id, templateKey: null, title: report.name, position: reportSections.length, width: 2, height: 2, visualization }]);
    },
    onSuccess: (result) => {
      if (result === false) return;
      refreshDashboard();
      scrollToNewReportCard();
      toast.success('Custom report added to dashboard');
    },
  });

  const reset = useMutation({
    mutationFn: () => analyticsApi.post(`/analytics/dashboards/${dashboard?.id}/reset`, { version: dashboard?.version }),
    onSuccess: () => { refreshDashboard(); toast.success('Dashboard reset to default'); },
  });
  const saveCustomReport = useMutation({
    mutationFn: async (draft: ReportDraft) => {
      const report = (await analyticsApi.post<SavedReport>('/analytics/reports', draft)).data;
      try {
        const visualization = report.definition.visualization ?? 'table';
        if (!dashboard) {
          await analyticsApi.post('/analytics/dashboards', { name: surface === 'HOME' ? 'Home dashboard' : 'Marketing dashboard', surface, visibility: 'PRIVATE', sections: [{ reportId: report.id, position: 0, width: 2, height: 2, visualization }] });
        } else {
          const nextReportSections = [...reportSections, {
            id: `new-${report.id}`,
            reportId: report.id,
            templateKey: null,
            title: report.name,
            position: reportSections.length,
            width: 2,
            height: 2,
            visualization,
          }];
          await analyticsApi.post(`/analytics/dashboards/${dashboard.id}/sections`, {
            version: dashboard.version,
            sections: mergeSectionKind(dashboard.sections, nextReportSections, isReportSection)
              .map(toSectionPayload),
          });
        }
        return { report, added: true };
      } catch {
        return { report, added: false };
      }
    },
    onSuccess: ({ added }) => {
      client.invalidateQueries({ queryKey: ['analytics', 'saved-reports'] });
      refreshDashboard();
      setEditingTemplate(null);
      if (added) { scrollToNewReportCard(); toast.success('Custom report created and added to dashboard'); }
      else toast.warning('Report was saved, but the dashboard changed. Add it from My reports.');
    },
    onError: () => toast.error('Custom report could not be created.'),
  });
  const updateReportVisibility = useMutation({
    mutationFn: async (report: SavedReport) => (
      await analyticsApi.patch<SavedReport>(`/analytics/reports/${report.id}`, {
        version: report.version,
        visibility: report.visibility === 'ORGANIZATION' ? 'PRIVATE' : 'ORGANIZATION',
      })
    ).data,
    onSuccess: (updated) => {
      client.setQueryData<SavedReport[]>(['analytics', 'saved-reports'], (current) => (
        current?.map((report) => report.id === updated.id ? updated : report)
      ));
      toast.success(updated.visibility === 'ORGANIZATION' ? 'Report shared with the organization' : 'Report made private');
    },
    onError: () => {
      client.invalidateQueries({ queryKey: ['analytics', 'saved-reports'] });
      toast.error('Report sharing could not be updated. Reload and retry.');
    },
  });

  if (!canCustomize) {
    return (
      <aside aria-label={surface === 'HOME' ? 'Home dashboard report library' : 'Report library'} className={mode === 'drawer' ? 'h-full w-full min-w-0' : 'w-screen min-w-screen sm:w-[300px] sm:min-w-[300px]'} style={{ height: '100vh', padding: 16, background: 'var(--side-bg)', borderRight: '1px solid var(--side-line, var(--border-subtle))' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>View-only analytics</div>
        <p style={{ marginTop: 6, fontSize: 12, lineHeight: 1.5, color: 'var(--text-tertiary)' }}>Your role can view and export reports, but cannot change dashboard layouts or report visibility.</p>
        {onClose && <button type="button" className="pulse-btn" onClick={onClose}>Close</button>}
      </aside>
    );
  }

  const query = search.trim().toLowerCase();
  const filtered = (templates.data ?? []).filter((item) => !query || `${item.name} ${item.description} ${item.category}`.toLowerCase().includes(query));
  const grouped = Object.entries(filtered.reduce<Record<string, ReportTemplate[]>>((result, template) => {
    (result[template.category] ??= []).push(template);
    return result;
  }, {}));

  const move = (index: number, direction: -1 | 1): void => {
    if (!dashboard) return;
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= reportSections.length) return;
    const sections = [...reportSections];
    [sections[index], sections[nextIndex]] = [sections[nextIndex]!, sections[index]!];
    saveLayout.mutate(sections);
  };
  const snapshotAnalyticsWidgetLayout = (): AnalyticsWidgetLayoutSnapshot => ({
    ids: analyticsWidgetIds,
    sizes: Object.fromEntries(analyticsWidgetIds.map((id) => {
      const layout = analyticsWidgetLayouts.get(id);
      return [id, { width: normalizeAnalyticsWidgetWidth(id, layout?.width), height: normalizeAnalyticsWidgetHeight(id, layout?.height) }];
    })) as Partial<Record<AnalyticsWidgetId, { width: number; height: number }>>,
  });
  const addAnalyticsCard = (id: AnalyticsWidgetId): void => {
    const next = [...analyticsWidgetIds.filter((item) => item !== id), id];
    setPreviousAnalyticsWidgetLayout(snapshotAnalyticsWidgetLayout());
    saveWidgetLayout.mutate({ ids: next });
  };
  const removeAnalyticsCard = (id: AnalyticsWidgetId): void => {
    setPreviousAnalyticsWidgetLayout(snapshotAnalyticsWidgetLayout());
    saveWidgetLayout.mutate({ ids: analyticsWidgetIds.filter((item) => item !== id) });
  };
  const showAllAnalyticsCards = (): void => {
    const next: AnalyticsWidgetId[] = [...DEFAULT_ANALYTICS_WIDGET_IDS];
    setPreviousAnalyticsWidgetLayout(snapshotAnalyticsWidgetLayout());
    saveWidgetLayout.mutate({ ids: next });
  };
  const resizeAnalyticsCard = (id: AnalyticsWidgetId, width: number): void => {
    const option = ANALYTICS_WIDGET_SIZE_OPTIONS.find((item) => item.width === width);
    if (!option) return;
    setPreviousAnalyticsWidgetLayout(snapshotAnalyticsWidgetLayout());
    saveWidgetLayout.mutate({ ids: analyticsWidgetIds, overrides: { [id]: { width: option.width, height: option.height } } });
  };
  const undoAnalyticsCardChange = (): void => {
    if (!previousAnalyticsWidgetLayout) return;
    saveWidgetLayout.mutate({ ids: previousAnalyticsWidgetLayout.ids, overrides: previousAnalyticsWidgetLayout.sizes });
    setPreviousAnalyticsWidgetLayout(null);
  };
  const widgetsByCategory = DEFAULT_ANALYTICS_WIDGETS.reduce<Record<AnalyticsWidgetCategory, typeof DEFAULT_ANALYTICS_WIDGETS[number][]>>((groups, widget) => {
    (groups[widget.category] ??= []).push(widget);
    return groups;
  }, {} as Record<AnalyticsWidgetCategory, typeof DEFAULT_ANALYTICS_WIDGETS[number][]>);

  return (
    <aside aria-label={surface === 'HOME' ? 'Home dashboard report library' : 'Report library'} className={mode === 'drawer' ? 'h-full w-full min-w-0' : 'w-screen min-w-screen sm:w-[300px] sm:min-w-[300px]'} style={{ height: '100vh', overflow: 'auto', padding: 12, background: 'var(--side-bg)', borderRight: '1px solid var(--side-line, var(--border-subtle))' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div><div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{surface === 'HOME' ? 'Customize dashboard' : 'Customize report'}</div><div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Add and arrange dashboard cards</div></div>
        {onClose && <IconButton label="Close customizer" onClick={onClose}><X className="h-4 w-4" /></IconButton>}
      </div>
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <Search className="h-4 w-4" style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-tertiary)' }} />
        <input aria-label="Search reports" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search reports" style={{ width: '100%', height: 36, padding: '0 10px 0 32px', border: '1px solid var(--border-default)', borderRadius: 8, background: 'var(--surface-input, var(--surface-card))' }} />
      </div>

      {editingTemplate && catalog.data ? (
        <ReportEditor template={editingTemplate} catalog={catalog.data} saving={saveCustomReport.isPending} onCancel={() => setEditingTemplate(null)} onSave={(draft) => saveCustomReport.mutate(draft)} />
      ) : null}

      {!editingTemplate && surface === 'ANALYTICS' && (
        <LibraryGroup title="Analytics cards" icon={LayoutGrid} action={<button type="button" className="pulse-btn sm" aria-label="Show all default analytics cards" onClick={showAllAnalyticsCards} disabled={saveWidgetLayout.isPending}><RotateCcw className="h-3.5 w-3.5" /></button>}>
          {saveWidgetLayout.isPending && <div style={{ marginBottom: 6, fontSize: 11, color: 'var(--text-tertiary)' }}>Saving card layout…</div>}
          {previousAnalyticsWidgetLayout && <button type="button" className="pulse-btn sm" style={{ width: '100%', marginBottom: 8 }} onClick={undoAnalyticsCardChange} disabled={saveWidgetLayout.isPending}>Undo last card change</button>}
          {(['Revenue', 'Channels', 'Audience', 'Email quality'] as AnalyticsWidgetCategory[]).map((category) => (
            <div key={category} style={{ marginTop: 10 }}>
              <div style={subGroupLabel}>{category}</div>
              {(widgetsByCategory[category] ?? []).map((widget) => {
                const added = analyticsWidgetIdSet.has(widget.id);
                const layout = analyticsWidgetLayouts.get(widget.id);
                const width = normalizeAnalyticsWidgetWidth(widget.id, layout?.width);
                return (
                  <div key={widget.id} style={{ ...rowStyle, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', alignItems: 'center', gap: 6 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{widget.title}</span>
                        <StatusBadge tone={added ? 'shown' : 'hidden'}>{added ? 'Shown' : 'Hidden'}</StatusBadge>
                      </div>
                      {added && (
                        <select aria-label={`Size ${widget.title}`} value={width} onChange={(event) => resizeAnalyticsCard(widget.id, Number(event.target.value))} disabled={saveWidgetLayout.isPending} style={sizeSelectStyle}>
                          {ANALYTICS_WIDGET_SIZE_OPTIONS.map((option) => <option key={option.width} value={option.width}>{option.label}</option>)}
                        </select>
                      )}
                    </div>
                    <IconButton label={added ? `Hide ${widget.title}` : `Show ${widget.title}`} disabled={saveWidgetLayout.isPending} onClick={() => added ? removeAnalyticsCard(widget.id) : addAnalyticsCard(widget.id)}>
                      {added ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                    </IconButton>
                  </div>
                );
              })}
            </div>
          ))}
        </LibraryGroup>
      )}

      {!editingTemplate && (reports.data?.length ?? 0) > 0 && <LibraryGroup title="My reports" icon={Star}>{reports.data!.map((report) => {
        const available = reportMetricsAvailable(report, catalog.data);
        const added = reportIsAdded(report.id);
        const isOwner = report.ownerUserId === userId;
        return (
          <div key={report.id} className="pulse-report-row" title={available ? undefined : 'This saved report uses metrics that are not available yet.'} style={{ ...rowStyle, display: 'flex', alignItems: 'center', gap: 8, opacity: available ? 1 : 0.6 }}>
            <ReportTypeIcon visualization={report.definition.visualization} />
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{report.name}</span>
            {report.visibility === 'ORGANIZATION' && <Badge tone="shared">Shared</Badge>}
            {!available && <Badge tone="muted">Unavailable</Badge>}
            {added && <Badge tone="added">Added</Badge>}
            {isOwner && <IconButton label={report.visibility === 'ORGANIZATION' ? `Make ${report.name} private` : `Share ${report.name} with organization`} disabled={updateReportVisibility.isPending} onClick={() => updateReportVisibility.mutate(report)}>{report.visibility === 'ORGANIZATION' ? <Lock className="h-3.5 w-3.5" /> : <Share2 className="h-3.5 w-3.5" />}</IconButton>}
            <IconButton label={added ? 'Custom report already on dashboard' : 'Add custom report to dashboard'} disabled={!available || added} onClick={() => addSavedReport.mutate(report)}><Plus className="h-3.5 w-3.5" /></IconButton>
          </div>
        );
      })}</LibraryGroup>}
      {!editingTemplate && grouped.map(([category, items]) => (
        <LibraryGroup key={category} title={labelize(category)} icon={categoryIcon(category)}>
          {items.map((template) => (
            <div key={template.key} className="pulse-report-row" title={template.available ? template.description : 'This report is planned but its semantic query is not available yet.'} style={{ ...rowStyle, display: 'flex', alignItems: 'center', gap: 8, opacity: template.available ? 1 : 0.6 }}>
              <ReportTypeIcon visualization={template.visualization} />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{template.name}</span>
              {!template.available && <Badge tone="soon">Soon</Badge>}
              {templateIsAdded(template.key) && <Badge tone="added">Added</Badge>}
              <IconButton label="Customize and save report" disabled={!template.available} onClick={() => setEditingTemplate(template)}><Copy className="h-3.5 w-3.5" /></IconButton>
              <IconButton label={templateIsAdded(template.key) ? 'Already on dashboard' : 'Add to dashboard'} disabled={!template.available || templateIsAdded(template.key)} onClick={() => addTemplate.mutate(template)}><Plus className="h-3.5 w-3.5" /></IconButton>
            </div>
          ))}
        </LibraryGroup>
      ))}

      {!editingTemplate && dashboard && (
        <LibraryGroup title="Dashboard layout" icon={LayoutGrid} action={<button type="button" className="pulse-btn sm" aria-label="Reset dashboard to default" onClick={() => reset.mutate()} disabled={reset.isPending}><RotateCcw className="h-3.5 w-3.5" /></button>}>
          {reportSections.map((section, index) => (
            <div key={section.id} style={{ ...rowStyle, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ flex: 1 }}>{section.title ?? templateName(templates.data, section.templateKey) ?? 'Saved report'}</span>
              <IconButton label="Move up" disabled={index === 0} onClick={() => move(index, -1)}><ArrowUp className="h-3 w-3" /></IconButton>
              <IconButton label="Move down" disabled={index === reportSections.length - 1} onClick={() => move(index, 1)}><ArrowDown className="h-3 w-3" /></IconButton>
              <IconButton label="Remove" onClick={() => saveLayout.mutate(reportSections.filter((_, itemIndex) => itemIndex !== index))}><X className="h-3 w-3" /></IconButton>
            </div>
          ))}
        </LibraryGroup>
      )}
    </aside>
  );
}

function ReportEditor({ template, catalog, saving, onCancel, onSave }: { template: ReportTemplate; catalog: MetricCatalog; saving: boolean; onCancel: () => void; onSave: (draft: ReportDraft) => void }): React.JSX.Element {
  const [name, setName] = React.useState(`${template.name} — custom`);
  const [metrics, setMetrics] = React.useState<string[]>(template.metrics);
  const [dimensions, setDimensions] = React.useState<string[]>(template.dimensions);
  const [visualization, setVisualization] = React.useState(template.visualization);
  const toggle = (items: string[], key: string, max: number): string[] => items.includes(key) ? items.filter((item) => item !== key) : items.length < max ? [...items, key] : items;
  const selectedDefinitions = catalog.metrics.filter((metric) => metrics.includes(metric.key));
  const selectedSource = selectedDefinitions[0]?.source;
  const compatibleDimensionKeys = new Set(catalog.dimensions.filter((dimension) => selectedDefinitions.every((metric) => metric.dimensions.includes(dimension.key))).map((dimension) => dimension.key));
  const disabledMetricCount = catalog.metrics.filter((metric) => selectedSource && metric.source !== selectedSource).length;
  const incompatibleDimensionCount = catalog.dimensions.filter((dimension) => !compatibleDimensionKeys.has(dimension.key)).length;
  const selectedMetricNames = selectedDefinitions.map((metric) => metric.name);
  const selectedDimensionNames = catalog.dimensions.filter((dimension) => dimensions.includes(dimension.key)).map((dimension) => dimension.name);
  React.useEffect(() => {
    setDimensions((current) => current.filter((dimension) => compatibleDimensionKeys.has(dimension)));
  }, [metrics.join('|')]); // Reconcile grouping whenever the governed metric set changes.
  return (
    <section style={{ display: 'grid', gap: 14 }}>
      <div><label style={editorLabel}>Report name</label><input value={name} onChange={(event) => setName(event.target.value)} style={editorInput} /></div>
      <div><label style={editorLabel}>Visualization</label><select value={visualization} onChange={(event) => setVisualization(event.target.value)} style={editorInput}>{['kpi', 'line', 'bar', 'donut', 'table'].map((item) => <option key={item} value={item}>{labelize(item)}</option>)}</select></div>
      <EditorOptions title={selectedSource ? `Metrics · ${labelize(selectedSource)}` : 'Metrics'} items={catalog.metrics} selected={metrics} disabledKeys={new Set(catalog.metrics.filter((metric) => selectedSource && metric.source !== selectedSource).map((metric) => metric.key))} onToggle={(key) => setMetrics((current) => toggle(current, key, 12))} />
      {selectedSource && disabledMetricCount > 0 ? <EditorNotice>{disabledMetricCount} metrics are locked because this report currently uses the {labelize(selectedSource)} data source.</EditorNotice> : null}
      <EditorOptions title="Group by" items={catalog.dimensions} selected={dimensions} disabledKeys={new Set(catalog.dimensions.filter((dimension) => !compatibleDimensionKeys.has(dimension.key)).map((dimension) => dimension.key))} onToggle={(key) => setDimensions((current) => toggle(current, key, 3))} />
      {incompatibleDimensionCount > 0 ? <EditorNotice>{incompatibleDimensionCount} dimensions are hidden from selection because every selected metric must support the same grouping.</EditorNotice> : null}
      <ReportBuildPreview visualization={visualization} metrics={selectedMetricNames} dimensions={selectedDimensionNames} />
      <div style={{ display: 'flex', gap: 8 }}><button type="button" className="pulse-btn" onClick={onCancel}>Cancel</button><button type="button" className="pulse-btn accent" disabled={saving || !name.trim() || metrics.length === 0} onClick={() => onSave({ name: name.trim(), description: template.description, templateKey: template.key, metrics, dimensions, visualization, visibility: 'PRIVATE' })}>Save report</button></div>
    </section>
  );
}

function EditorNotice({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div style={{ marginTop: -8, padding: '8px 10px', border: '1px solid var(--border-subtle)', borderRadius: 9, background: 'var(--bg-muted)', color: 'var(--text-tertiary)', fontSize: 11, lineHeight: 1.45 }}>{children}</div>;
}

function ReportBuildPreview({ visualization, metrics, dimensions }: { visualization: string; metrics: string[]; dimensions: string[] }): React.JSX.Element {
  return (
    <section aria-label="Report build preview" style={{ padding: 10, border: '1px solid var(--border-subtle)', borderRadius: 10, background: 'var(--surface-card)' }}>
      <div style={{ marginBottom: 7, fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>Preview before save</div>
      <div style={{ display: 'grid', gap: 5, color: 'var(--text-tertiary)', fontSize: 11, lineHeight: 1.45 }}>
        <span><strong style={{ color: 'var(--text-secondary)' }}>{labelize(visualization)}</strong> card with {metrics.length} metric{metrics.length === 1 ? '' : 's'}</span>
        <span>Metrics: {metrics.length > 0 ? metrics.slice(0, 4).join(', ') : 'Select at least one metric'}{metrics.length > 4 ? ` +${metrics.length - 4} more` : ''}</span>
        <span>Grouped by: {dimensions.length > 0 ? dimensions.join(', ') : 'No dimension'}</span>
      </div>
    </section>
  );
}

function EditorOptions({ title, items, selected, disabledKeys = new Set(), onToggle }: { title: string; items: Array<{ key: string; name: string }>; selected: string[]; disabledKeys?: Set<string>; onToggle: (key: string) => void }): React.JSX.Element {
  const orderedItems = [...items].sort((left, right) => {
    const leftRank = selected.includes(left.key) ? 0 : disabledKeys.has(left.key) ? 2 : 1;
    const rightRank = selected.includes(right.key) ? 0 : disabledKeys.has(right.key) ? 2 : 1;
    return leftRank - rightRank;
  });
  return <fieldset style={{ border: 0, padding: 0, margin: 0 }}><legend style={editorLabel}>{title}</legend><div style={{ display: 'grid', gap: 5, maxHeight: 190, overflow: 'auto' }}>{orderedItems.map((item) => { const disabled = disabledKeys.has(item.key); return <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: disabled ? 'var(--text-tertiary)' : 'var(--text-secondary)', opacity: disabled ? 0.5 : 1 }}><input type="checkbox" checked={selected.includes(item.key)} disabled={disabled} onChange={() => onToggle(item.key)} />{item.name}</label>; })}</div></fieldset>;
}

function LibraryGroup({ title, icon: Icon, action, children }: { title: string; icon?: LucideIcon; action?: React.ReactNode; children: React.ReactNode }): React.JSX.Element {
  return (
    <section style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {Icon && <Icon className="h-3.5 w-3.5" style={{ color: 'var(--brand-default)' }} aria-hidden="true" />}
          {title}
        </span>
        {action}
      </div>
      {children}
    </section>
  );
}

const BADGE_TONES: Record<string, { bg: string; color: string }> = {
  added:   { bg: 'var(--brand-subtle)',   color: 'var(--brand-default)' },
  shown:   { bg: 'var(--success-muted)',  color: 'var(--success-default)' },
  hidden:  { bg: 'var(--bg-muted)',       color: 'var(--text-tertiary)' },
  soon:    { bg: 'var(--warning-subtle)', color: 'var(--warning-default)' },
  shared:  { bg: 'var(--info-subtle)',    color: 'var(--info-default)' },
  muted:   { bg: 'var(--bg-muted)',       color: 'var(--text-tertiary)' },
};

function Badge({ tone, children }: { tone: keyof typeof BADGE_TONES; children: React.ReactNode }): React.JSX.Element {
  const palette = BADGE_TONES[tone] ?? BADGE_TONES['muted']!;
  return <span style={{ ...statusBadgeStyle, background: palette.bg, color: palette.color }}>{children}</span>;
}

function StatusBadge({ tone, children }: { tone: 'shown' | 'hidden'; children: React.ReactNode }): React.JSX.Element {
  return <Badge tone={tone}>{children}</Badge>;
}

const VIZ_ICON: Record<string, LucideIcon> = {
  kpi: Hash,
  line: LineChart,
  bar: BarChart3,
  donut: PieChart,
  table: Table,
};

function ReportTypeIcon({ visualization }: { visualization: string | undefined }): React.JSX.Element {
  const Icon = VIZ_ICON[visualization ?? 'table'] ?? Table;
  return (
    <span className="pulse-report-type" aria-hidden="true">
      <Icon className="h-3.5 w-3.5" />
    </span>
  );
}

function IconButton({ label, disabled, onClick, children }: { label: string; disabled?: boolean; onClick: () => void; children: React.ReactNode }): React.JSX.Element {
  const button = (
    <button type="button" aria-label={label} disabled={disabled} onClick={onClick} className="pulse-icon-btn" data-disabled={disabled ? '' : undefined}>
      {children}
    </button>
  );
  if (disabled) return button;
  return <Tooltip content={label}>{button}</Tooltip>;
}

const rowStyle: React.CSSProperties = { padding: '9px 8px', fontSize: 13, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-subtle)' };
const subGroupLabel: React.CSSProperties = { marginBottom: 4, fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' };
const statusBadgeStyle: React.CSSProperties = { borderRadius: 999, padding: '2px 8px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' };
const sizeSelectStyle: React.CSSProperties = { marginTop: 5, width: '100%', height: 28, borderRadius: 7, border: '1px solid var(--border-subtle)', background: 'var(--surface-card)', color: 'var(--text-secondary)', fontSize: 11 };
const editorLabel: React.CSSProperties = { display: 'block', marginBottom: 6, fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' };
const editorInput: React.CSSProperties = { width: '100%', height: 36, padding: '0 10px', border: '1px solid var(--border-default)', borderRadius: 8, background: 'var(--surface-card)', color: 'var(--text-primary)' };
const labelize = (value: string): string => value.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
const CATEGORY_ICON: Record<string, LucideIcon> = {
  acquisition: Globe,
  behavior: Activity,
  customers: Users,
  finances: DollarSign,
  products: Package,
  campaigns: Sparkles,
};
const categoryIcon = (category: string): LucideIcon => CATEGORY_ICON[category.toLowerCase()] ?? LayoutGrid;
// Newly added report cards append to the end of the page grid — bring them into
// view so the user sees the change happen behind the (lightly dimmed) drawer.
function scrollToNewReportCard(): void {
  window.setTimeout(() => {
    const target = document.querySelector('main') ?? document.scrollingElement;
    target?.scrollTo({ top: target.scrollHeight, behavior: 'smooth' });
  }, 450);
}
const templateName = (templates: ReportTemplate[] | undefined, key: string | null): string | undefined => templates?.find((template) => template.key === key)?.name;
const isReportSection = (section: DashboardSection): boolean => Boolean(section.reportId || section.templateKey);
const isWidgetSection = (section: DashboardSection): boolean => Boolean(section.layout?.widgetKey);
const mergeSectionKind = (
  currentSections: DashboardSection[],
  replacementSections: DashboardSection[],
  matchesKind: (section: DashboardSection) => boolean,
): DashboardSection[] => {
  const replacements = [...replacementSections];
  const merged = [...currentSections]
    .sort((left, right) => left.position - right.position)
    .flatMap((section) => {
      if (!matchesKind(section)) return [section];
      const replacement = replacements.shift();
      return replacement ? [replacement] : [];
    });
  return [...merged, ...replacements].map((section, position) => ({ ...section, position }));
};
const widgetLayoutById = (dashboard: Dashboard | undefined): Map<AnalyticsWidgetId, DashboardSection> => new Map((dashboard?.sections ?? [])
  .filter(isWidgetSection)
  .flatMap((section): Array<[AnalyticsWidgetId, DashboardSection]> => {
    const widgetKey = section.layout?.widgetKey;
    return widgetKey && ANALYTICS_WIDGET_ID_SET.has(widgetKey) ? [[widgetKey as AnalyticsWidgetId, section]] : [];
  }));
const widgetOrderFromDashboard = (dashboard: Dashboard | undefined): AnalyticsWidgetId[] => {
  const persisted = (dashboard?.sections ?? [])
    .filter(isWidgetSection)
    .sort((left, right) => left.position - right.position)
    .map((section) => section.layout?.widgetKey)
    .filter((id): id is AnalyticsWidgetId => Boolean(id && ANALYTICS_WIDGET_ID_SET.has(id)));
  if (persisted.length === 0) return [...DEFAULT_ANALYTICS_WIDGET_IDS];
  return persisted;
};
const widgetSectionsFromOrder = (ids: AnalyticsWidgetId[], existingSections: DashboardSection[] = [], overrides: Partial<Record<AnalyticsWidgetId, { width: number; height: number }>> = {}): DashboardSection[] => ids.map((id, position) => {
  const definition = getAnalyticsWidgetDefinition(id);
  const existing = existingSections.find((section) => section.layout?.widgetKey === id);
  const override = overrides[id];
  const width = override?.width ?? normalizeAnalyticsWidgetWidth(id, existing?.width);
  const height = override?.height ?? normalizeAnalyticsWidgetHeight(id, existing?.height);
  return {
    id: `widget-${id}`,
    reportId: null,
    templateKey: null,
    title: definition.title,
    position,
    width,
    height,
    visualization: 'kpi',
    layout: { widgetKey: id },
  };
});
const toSectionPayload = (section: DashboardSection, position: number): { reportId?: string; templateKey?: string; widgetKey?: string; title?: string; position: number; width: number; height: number; visualization: string } => ({
  ...(section.reportId ? { reportId: section.reportId } : {}),
  ...(section.templateKey ? { templateKey: section.templateKey } : {}),
  ...(section.layout?.widgetKey ? { widgetKey: section.layout.widgetKey } : {}),
  ...(section.title ? { title: section.title } : {}),
  position,
  width: section.width,
  height: section.height,
  visualization: section.visualization,
});
const reportMetricsAvailable = (report: SavedReport, catalog: MetricCatalog | undefined): boolean => {
  if (!catalog) return false;
  const available = new Set(catalog.metrics.map((metric) => metric.key));
  return (report.definition.metrics ?? []).every((metric) => available.has(metric));
};
